import React from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import ConnectionPopup from './ConnectionPopup';
import SparkInfiniteScroll from 'sharedComponents/UI/SparkInfiniteScroll';
import HistoryCardSkeleton from 'sharedComponents/Skeleton/HistoryCardSkeleton';
import PluginConnectionEditableField from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PluginFunctions/PluginConnectionAuthentication/PluginConnectionEditableField';
import {
  ErrorStatusIcon,
  SortAscIcon,
  SortDescIcon,
  SuccessStatusIcon,
} from 'components/SVG';
import { formatDateTimeMonth } from 'utilities/time';
import { useInfiniteConnections } from 'hooks/Plugins/useInfiniteConnections';
import { useUpdatePluginConnection } from 'hooks/SparkBuilder/pluginFunction';
import type { Plugins } from 'types/Plugins';
import { InfoCard } from 'sharedComponents/UI';

type sortFields = 'name' | 'lastModified';

type PluginConnectionsProps = {
  plugin: Plugins;
  isOpen: boolean;
};

type sortOptions = {
  sortOrder: 'asc' | 'desc';
  sortField: sortFields;
};

const SORTABLE_FIELDS = {
  NAME: 'name',
  LAST_MODIFIED: 'lastModified',
} as const;

function PluginConnections({ plugin, isOpen }: PluginConnectionsProps) {
  const [sortConfig, setSortConfig] = React.useState<sortOptions>({
    sortOrder: 'asc',
    sortField: SORTABLE_FIELDS.NAME,
  });
  const [connectionStatusInfo, setConnectionStatusInfo] = React.useState<{
    connectionStatus?: { type: string; message: string };
    isLoading: boolean;
    connectionID?: string;
  }>();

  const { mutateAsync: updatePluginConnection } = useUpdatePluginConnection();

  const [editingConnectionID, setEditingConnectionID] = React.useState<
    string | null
  >('');
  const {
    connections,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isConnectionsLoading,
    fetchNextPage,
  } = useInfiniteConnections(plugin.id, isOpen);

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleConnectionNameEdit = async (
    connectionID: string,
    updatedConnectionName: string
  ) => {
    await updatePluginConnection({
      pluginConnectionID: connectionID,
      name: updatedConnectionName,
    });
  };

  const sortedConnections = React.useMemo(() => {
    if (!connections) return null;

    const { sortField, sortOrder } = sortConfig;

    return [...connections].sort((a, b) => {
      const aValue =
        sortField === SORTABLE_FIELDS.NAME
          ? a.pluginConnection.attributes.name || ''
          : a.pluginConnection.attributes.modifiedAt || '';
      const bValue =
        sortField === SORTABLE_FIELDS.NAME
          ? b.pluginConnection.attributes.name || ''
          : b.pluginConnection.attributes.modifiedAt || '';

      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [connections, sortConfig]);

  const handleSortConnections = (fieldName: sortFields) => {
    setSortConfig((prevSortOrder) => {
      return {
        sortOrder: prevSortOrder.sortOrder === 'asc' ? 'desc' : 'asc',
        sortField: fieldName,
      };
    });
  };

  const IconButtonElement = ({ fieldName }: { fieldName: sortFields }) => {
    return (
      <IconButton
        sx={{ padding: '5px' }}
        onClick={() => handleSortConnections(fieldName)}
      >
        {sortConfig.sortField === fieldName &&
        sortConfig.sortOrder === 'asc' ? (
          <SortAscIcon width='12px' height='12px' />
        ) : (
          <SortDescIcon width='12px' height='12px' />
        )}
      </IconButton>
    );
  };

  return (
    <>
      <Grid container>
        <Grid item xs={4} display={'flex'}>
          <Typography
            pl={7}
            sx={{
              fontSize: '16px',
              color: '#404040',
              fontFamily: 'Mont-SemiBold',
              cursor: 'pointer',
            }}
            onClick={() => handleSortConnections(SORTABLE_FIELDS.NAME)}
          >
            Name
          </Typography>
          {sortConfig.sortField === 'name' && (
            <IconButtonElement fieldName={SORTABLE_FIELDS.NAME} />
          )}
        </Grid>
        <Grid item xs={6} display={'flex'}>
          <Typography
            onClick={() => handleSortConnections(SORTABLE_FIELDS.LAST_MODIFIED)}
            sx={{
              fontSize: '16px',
              color: '#404040',
              fontFamily: 'Mont-SemiBold',
            }}
          >
            Last Modified
          </Typography>
          {sortConfig.sortField === 'lastModified' && (
            <IconButtonElement fieldName={SORTABLE_FIELDS.LAST_MODIFIED} />
          )}
        </Grid>
      </Grid>
      <SparkInfiniteScroll
        isLoading={isConnectionsLoading}
        onLastElementView={handleLatElementView}
        childContainerProps={{ container: true, paddingRight: '10px' }}
        containerStyles={{
          maxHeight: '20rem',
        }}
        loadingContent={
          <Box mt={2} width={'100%'}>
            <HistoryCardSkeleton count={1} />
          </Box>
        }
        skeletonCount={10}
      >
        {sortedConnections?.map(
          ({ pluginConnection: connection, isConnectionValid }) => {
            const { name: connectionName, modifiedAt } = connection.attributes;
            const formattedDate = formatDateTimeMonth(
              modifiedAt,
              'D MMMM YYYY, h:mm:ss a'
            );
            return (
              <Grid container item xs={12} gap={2} key={connection.id}>
                <Grid
                  container
                  p={2}
                  display={'flex'}
                  bgcolor={(theme) => theme.palette.common.white}
                  border={(theme) => `1px solid ${theme.palette.borderColor}`}
                  borderRadius='10px'
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}
                  mb={2}
                >
                  <Grid item xs={4}>
                    <Box display={'flex'} alignItems={'center'}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {isConnectionValid ? (
                          <SuccessStatusIcon />
                        ) : (
                          <ErrorStatusIcon />
                        )}
                      </Avatar>
                      <PluginConnectionEditableField
                        value={connectionName}
                        onTitleSave={(updatedValue) =>
                          handleConnectionNameEdit(connection.id, updatedValue)
                        }
                        setIsEditing={(isEditing) =>
                          setEditingConnectionID(
                            isEditing ? connection.id : null
                          )
                        }
                        isEditing={editingConnectionID === connection.id}
                        fullWidth={true}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>{formattedDate}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    display={'flex'}
                    justifyContent={'flex-end'}
                    alignItems={'center'}
                  >
                    {connectionStatusInfo?.connectionID === connection.id ? (
                      connectionStatusInfo.isLoading ? (
                        <InfoCard
                          info='Testing connection'
                          cardStyles={{ width: '100%' }}
                          infoStyles={{ alignItems: 'center' }}
                          customIcon={
                            <CircularProgress
                              variant='indeterminate'
                              thickness={8}
                              sx={{
                                width: `20px !important`,
                                height: `20px !important`,
                              }}
                            />
                          }
                        />
                      ) : (
                        !!connectionStatusInfo && (
                          <InfoCard
                            type={
                              connectionStatusInfo.connectionStatus?.type as any
                            }
                            info={
                              connectionStatusInfo.connectionStatus?.message
                            }
                            cardStyles={{ width: '100%' }}
                          />
                        )
                      )
                    ) : (
                      <></>
                    )}
                    <ConnectionPopup
                      selectedPlugin={plugin}
                      setIsEditing={setEditingConnectionID}
                      connection={connection}
                      setConnectionStatusInfo={setConnectionStatusInfo}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          }
        )}
      </SparkInfiniteScroll>
    </>
  );
}

export default PluginConnections;
